import { useEffect } from 'react';
import type { ShouldRevalidateFunctionArgs } from 'react-router';

import { apiServerFetchSynonymSearchApiKey } from '#/api/apiFetchSynonymSearchApiKey';
import { apiDeleteTmpStorage } from '#/api/tmpStorage/apiDeleteTmpStorage';
import { apiServerFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { useQueryParamState } from '#/hooks/useQueryParamState';
import { useTrackPageView } from '#/hooks/useTrackPageView';
import { RouteModule } from '#/horizon/components/RouteModule';
import type { LoaderDataWithOverrideLocale } from '#/horizon/utils/getMatchesOverrideLocale';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { salaryApiServerFetchRecentSubmissions } from '#/modules/salary/api/salaryApiFetchRecentSubmissions';
import { salaryApiServerFetchCompensationSurvey } from '#/modules/salary/api/salaryApiGetCompensationSurvey';
import { salaryApiServerFetchTmpStorage } from '#/modules/salary/api/tmpStorage/salaryApiFetchTmpStorage';
import { salariesExploreRoute } from '#/modules/salary/routing/salary.routes';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getAppRouteSearchParams } from '#/routing/utils/getAppRouteSearchParams';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SalariesExplorePageAuth } from './Auth/SalariesExplorePageAuth';
import { SalaryExplorePage } from './SalaryExplorePage';

export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  const removedExploreTmpId =
    currentUrl.searchParams.has('exploreTmpId') &&
    !nextUrl.searchParams.has('exploreTmpId');

  if (removedExploreTmpId) return false;

  return defaultShouldRevalidate;
}

export async function loader({
  request,
  url,
  context,
}: HorizonAppRouteLoaderArgs<typeof salariesExploreRoute>) {
  const sharedHandle = {
    defaultSearchType: 'JOB',
    hideFooterDivider: true,
    showDesktopSearch: true,
  } satisfies LayoutHandleProps;

  const { exploreTmpId } = getAppRouteSearchParams({
    route: salariesExploreRoute,
    url,
  });

  const { user } = await apiServerFetchUserEnvironment({
    request,
    context,
  })();

  if (!user) {
    return {
      handle: sharedHandle,
      type: 'not-authenticated',
      overrideLocale: I18nLocale.EN,
    } as const satisfies LoaderDataWithOverrideLocale & Record<string, unknown>;
  }

  const [searchApiKey, compensationSurvey, recentSubmissions, tmpStorage] =
    await Promise.all([
      apiServerFetchSynonymSearchApiKey({ request, context })(),
      salaryApiServerFetchCompensationSurvey({ request, context })(),
      salaryApiServerFetchRecentSubmissions({ request, context })(),
      exploreTmpId
        ? salaryApiServerFetchTmpStorage({ request, context })(exploreTmpId)
        : null,
    ]);

  return {
    handle: sharedHandle,
    type: 'authenticated',
    searchApiKey,
    alreadySubmitted: compensationSurvey.alreadySubmitted,
    salaryModelCreated:
      Date.parse(compensationSurvey.salaryModelCreated) / 1000,
    recentSubmissions,
    overrideLocale: I18nLocale.EN,
    initialSurveyReportFormData: tmpStorage?.surveyReport ?? null,
  } as const satisfies LoaderDataWithOverrideLocale & Record<string, unknown>;
}

export default function SalaryExploreHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();
  useTrackPageView('Explore Salaries');

  const [tmpId, setTmpId] = useQueryParamState({ name: 'tmpId' });
  useEffect(() => {
    if (tmpId && loaderData.initialSurveyReportFormData) {
      setTmpId(undefined, { replace: true });
      void apiDeleteTmpStorage(tmpId);
    }
  }, [tmpId, loaderData.initialSurveyReportFormData, setTmpId]);

  return (
    <RouteModule
      title={i18n('Explore Nonprofit Salaries')}
      ogImage={getStaticImageSrc('compdb/og-image.png')}
    >
      {loaderData.type === 'authenticated' ? (
        <SalaryExplorePage
          initialSearchApiKey={loaderData.searchApiKey}
          recentSubmissions={loaderData.recentSubmissions}
          salaryModelCreated={loaderData.salaryModelCreated}
          alreadySubmitted={loaderData.alreadySubmitted}
          initialSurveyReportFormData={loaderData.initialSurveyReportFormData}
        />
      ) : (
        <SalariesExplorePageAuth />
      )}
    </RouteModule>
  );
}
